import { CheckCircle2, Loader2, Sparkles, XCircle } from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/tw-merge/cn';

import MarkdownText from './markdown-text';
import {
  getStreamableMessageContent,
  type StreamableChatMessage,
} from './streaming-chat-message';

type ActivityRow = {
  id: string;
  label: string;
  meta?: string;
  status?: string;
  success?: boolean;
  createdAt?: string;
  kind: 'progress' | 'tool';
};

interface StreamingAssistantMessageProps {
  message: StreamableChatMessage;
  className?: string;
  contentClassName?: string;
  activityClassName?: string;
}

const buildActivityRows = (message: StreamableChatMessage): ActivityRow[] => {
  if (!message.isStreaming) return [];

  const progressRows = (message.progressEvents ?? []).map(progressEvent => ({
    id: `progress-${progressEvent.id}`,
    label: progressEvent.text,
    meta:
      progressEvent.count && progressEvent.count > 1
        ? `${progressEvent.count}x`
        : undefined,
    status: progressEvent.status,
    createdAt: progressEvent.createdAt,
    kind: 'progress' as const,
  }));

  const toolRows = (message.toolEvents ?? []).map(toolEvent => ({
    id: `tool-${toolEvent.id}`,
    label: toolEvent.label || toolEvent.toolName || 'Working',
    meta: toolEvent.provider,
    status: toolEvent.status,
    success: toolEvent.success,
    createdAt: toolEvent.completedAt || toolEvent.startedAt,
    kind: 'tool' as const,
  }));

  return [...progressRows, ...toolRows]
    .sort((a, b) => {
      if (!a.createdAt || !b.createdAt) return 0;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    })
    .slice(-5);
};

const renderToolStatusIcon = (status?: string, success?: boolean) => {
  if (status === 'completed' || success === true) {
    return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />;
  }

  if (status === 'failed' || success === false) {
    return <XCircle className="h-3.5 w-3.5 text-red-500" />;
  }

  return <Loader2 className="h-3.5 w-3.5 animate-spin text-[#acb2bd]" />;
};

const renderActivityIcon = (row: ActivityRow, isActive: boolean) => {
  if (row.kind === 'tool') {
    return renderToolStatusIcon(row.status, row.success);
  }

  if (isActive) {
    return (
      <span className="agent-stream-dot relative flex h-3.5 w-3.5 items-center justify-center rounded-full">
        <span className="relative h-1.5 w-1.5 rounded-full bg-primary" />
      </span>
    );
  }

  return <CheckCircle2 className="h-3.5 w-3.5 text-gray-300" />;
};

export const StreamingAssistantMessage = ({
  message,
  className,
  contentClassName = 'text-xs leading-[24px] text-[var(--color-foreground)] sm:text-[15px]',
  activityClassName,
}: StreamingAssistantMessageProps) => {
  const content = getStreamableMessageContent(message);
  const shouldRenderContent = Boolean(content) && !message.isStreaming;
  const latestProgressText = message.progressText || 'Thinking';

  const activityRows = React.useMemo(() => {
    const rows = buildActivityRows(message);
    if (rows.length > 0) return rows;

    return [
      {
        id: 'thinking',
        label: latestProgressText,
        kind: 'progress' as const,
      },
    ];
  }, [latestProgressText, message]);

  const shouldShowStreamingActivity =
    message.isStreaming && (activityRows.length > 0 || !content);

  return (
    <div className={cn('min-w-0', className)}>
      {shouldShowStreamingActivity && (
        <div
          className={cn(
            'agent-stream-shell border-white/70 from-white/80 via-white/50 relative overflow-hidden rounded-[18px] border bg-gradient-to-br to-[#fff7f3]/40 px-3 py-3 shadow-[0_10px_26px_rgba(18,18,18,0.045)]',
            shouldRenderContent && 'mb-3',
            activityClassName
          )}
        >
          <div className="relative z-[1]">
            <div className="mb-2.5 flex min-w-0 items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="agent-stream-orb flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                </span>
                <div className="min-w-0">
                  <span className="agent-thinking-text block text-[11px] font-semibold uppercase leading-4 tracking-[0.08em]">
                    Give me a sec...
                  </span>
                </div>
              </div>

              <span className="shrink-0 rounded-full border border-primary/10 bg-primary/[0.06] px-2 py-0.5 text-[10px] font-semibold leading-4 text-primary">
                Live
              </span>
            </div>

            <div className="space-y-1.5">
              {activityRows.map((row, index, rows) => {
                const isActive = index === rows.length - 1;

                return (
                  <div
                    key={row.id}
                    className={cn(
                      'agent-stream-row relative flex min-w-0 items-start gap-2.5 rounded-[14px] px-2.5 py-1.5 text-xs leading-relaxed transition-all',
                      isActive
                        ? 'agent-stream-row-active bg-white/75 text-[#212833] shadow-[0_8px_20px_rgba(18,18,18,0.04)]'
                        : 'text-[#808894]'
                    )}
                  >
                    <span
                      className={cn(
                        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                        isActive
                          ? 'border-primary/15 bg-primary/[0.08]'
                          : 'border-white/80 bg-white/60'
                      )}
                    >
                      {renderActivityIcon(row, isActive)}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block break-words">{row.label}</span>
                      {row.meta && (
                        <span className="mt-0.5 inline-flex max-w-full rounded-full bg-[var(--color-cloud-100)] px-2 py-0.5 text-[10px] font-medium uppercase leading-4 tracking-normal text-[#8e96a6]">
                          {row.meta}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {shouldRenderContent && (
        <div data-chat-final-content-id={message.id}>
          <MarkdownText content={content} className={contentClassName} />
        </div>
      )}
    </div>
  );
};
